import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatCZK, formatCZKCompact } from "@/lib/format";
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, TrendingDown, Wallet, Package, FileText, Banknote, ClipboardList, Scale, Receipt, ArrowDownLeft, ArrowUpRight, List, PieChart as PieIcon, Info } from "lucide-react";
import EconomicsDocuments from "@/pages/EconomicsDocuments";
import { StatCard } from "@/components/shared/PagePrimitives";
import { ProjectTimeChart } from "@/components/economics/ProjectTimeChart";
import { useDocTags } from "@/components/economics/DocTags";
import { storage } from "@/lib/storage";

// Paleta donutů — CVD-checked pro světlý i tmavý režim; barvy jdou podle
// indexu kategorie a NEpřebarvují se, když nějaká kategorie chybí (129/C3).
const PIE_COLORS = ["#6996D4", "#B4823C", "#26A28C", "#9C7BD1", "#7C9A45", "#C56B84"];
const AMBER = "text-amber-600 dark:text-amber-400"; // přefakturace = varování, ne chyba
const TOOLTIP_STYLE = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" };
// 339: výseč koláče (Cell) nedává bublině barvu → Recharts píše položku černě, v tmavém režimu neviditelně
const TOOLTIP_ITEM = { color: "hsl(var(--foreground))" };

// 365: pohled záložky — Tabulka dokladů (výchozí) nebo Grafy a KPI; stejný přepínač jako v Úkolech
const VIEWS = [{ value: "tab", label: "Tabulka dokladů", icon: List }, { value: "graf", label: "Grafy a KPI", icon: PieIcon }];
function EcoViewSwitch({ value, onChange }) {
  return (
    <div className="flex h-11 p-1 bg-card border border-border rounded-full shrink-0" role="radiogroup" aria-label="Pohled" data-testid="eco-views">
      {VIEWS.map((v) => (
        <button key={v.value} type="button" role="radio" aria-checked={value === v.value} aria-label={v.label} title={v.label}
          onClick={() => onChange(v.value)} data-testid={`eco-view-${v.value}`}
          className={`h-full w-10 rounded-full grid place-items-center transition-colors motion-reduce:transition-none ${value === v.value ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}>
          <v.icon size={17} />
        </button>
      ))}
    </div>
  );
}

// Koláč s legendou (290: karty ve stylu Přehledu Ekonomiky — název, velké číslo, podtitulek)
function DonutCard({ title, info, total, sub, data, colorOf, testid, legendTestid, extra, empty }) {
  return (
    <div className="bg-card border border-border rounded-[20px] p-4 md:p-6 min-w-0" data-testid={testid}>
      <div className="text-[15px] font-medium text-muted-foreground flex items-center gap-1.5">{title}
        {info && <span title={info} aria-label={info} className="inline-flex text-muted-foreground/80"><Info size={15} /></span>}</div>
      <div className="font-heading font-extrabold tracking-tight tabular-nums whitespace-nowrap text-[28px] md:text-[36px] leading-tight mt-1"
        title={formatCZK(total)}>{formatCZKCompact(total)}</div>
      <div className="text-sm text-muted-foreground mt-0.5 mb-5">{sub}</div>
      {data.length > 0 ? (
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <div className="w-[190px] h-[190px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="90%"
                  paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                  {data.map((e, i) => <Cell key={e.name} fill={colorOf(e, i)} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${formatCZK(v)} · ${total ? Math.round((v / total) * 100) : 0} %`, n]} contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {/* Legenda s částkami musí zůstat čitelná i bez hoveru */}
          <div className="flex-1 w-full min-w-0 space-y-2" data-testid={legendTestid}>
            {data.map((e, i) => (
              <div key={e.name} className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorOf(e, i) }} />
                <span className="text-muted-foreground truncate">{e.name}</span>
                <span className="ml-auto font-semibold tabular-nums whitespace-nowrap">{formatCZK(e.value)}</span>
              </div>
            ))}
            {extra}
          </div>
        </div>
      ) : <p className="text-muted-foreground text-sm">{empty}</p>}
    </div>
  );
}

function KpiRow({ heading, icon: Icon, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
        {Icon && <Icon size={18} className="text-brand" />} {heading}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{children}</div>
    </div>
  );
}

// Ekonomika zakázky (220): jeden react-query klíč pro detail zakázky (pruh nahoře,
// ProjectDetail) i záložku Ekonomika — záložka nevolá /economics podruhé, když ho
// detail právě načetl nebo načítá.
export const projectEcoQuery = (id) => ({
  queryKey: ["project-eco", id],
  queryFn: () => api.get(`/projects/${id}/economics`).then((r) => r.data),
});
// Doklad uložený odjinud (globální „+", Sklad…) — data starší než poslední uložení
// se při otevření záložky načtou znovu, i kdyby byla mladší než staleTime.
let lastDocSaved = 0;
if (typeof window !== "undefined") window.addEventListener("pp-doc-saved", () => { lastDocSaved = Date.now(); });

export function ProjectEconomics({ projectId }) {
  const { data: eco, refetch, dataUpdatedAt } = useQuery({ ...projectEcoQuery(projectId), staleTime: 15_000, retry: false });
  const load = useCallback(() => { refetch(); }, [refetch]);
  useEffect(() => {
    if (dataUpdatedAt && dataUpdatedAt < lastDocSaved) refetch();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [view, setViewState] = useState(() => (storage.get("eco_view") === "graf" ? "graf" : "tab"));
  const setView = (v) => { setViewState(v); storage.set("eco_view", v); };
  const { colorOf: tagColor } = useDocTags(view === "graf");

  // 365: Tabulka = jen doklady zakázky (výchozí, přepínač vpravo v hlavičce dokladů),
  // Grafy = KPI a grafy bez tabulky
  const sw = <EcoViewSwitch value={view} onChange={setView} />;
  const bar = (
    <div className="flex items-center gap-3">
      <h3 className="font-bold flex items-center gap-2 min-w-0"><PieIcon size={18} className="text-brand shrink-0" /> <span className="truncate">KPI a grafy zakázky</span></h3>
      <span className="flex-1" />
      {sw}
    </div>
  );
  if (view === "tab") {
    return (
      <div className="space-y-4" data-testid="project-economics">
        {/* 166: doklady zakázky = stejná tabulka jako Ekonomika → Doklady, jen pro tuto
            zakázku; „Nahrát doklad" zakázku rovnou předvyplní. Uložení přepočte KPI. */}
        <EconomicsDocuments projectId={projectId} onChanged={load} headerExtra={sw} />
      </div>
    );
  }
  if (!eco) return <div className="space-y-4">{bar}<div className="text-muted-foreground">Načítám…</div></div>;

  const inv = eco.invoicing || {};
  // Dobropisy (167): kolik z vyfakturováno ubraly vydané dobropisy + kategorie, které
  // šly kreditem do mínusu (v koláči se nekreslí, ukážou se jako tlumená řádka).
  // 401: na stejném základu jako Vyfakturováno (plátce bez DPH) — dřív s DPH pod popiskem „bez DPH“
  const creditedInvoiced = (inv.invoices || []).filter((i) => i.is_credit_note).reduce((s, i) => s + (i.basis ?? i.total ?? 0), 0);
  const negCats = Object.values(eco.by_category || {}).filter((v) => v < 0).reduce((s, v) => s + v, 0);
  // Zadání 164: plátce DPH → náklady/výnosy na základu bez DPH; neplátce s DPH.
  // Inkasováno zůstává vždy reálná hotovost (s DPH), viz varianta (a).
  const vp = eco.basis !== "gross";
  const vatWord = vp ? "bez DPH" : "s DPH";
  const vat = eco.vat || {};
  const marginPositive = eco.margin >= 0;
  const plannedBudget = (eco.total_costs || 0) + (eco.remaining_budget || 0);
  const drawPct = plannedBudget > 0 ? Math.round((eco.total_costs / plannedBudget) * 100) : 0;
  const over = (inv.over_invoiced || 0) > 0;
  // 166/A1: zbytek k inkasu v gross proti gross platbám (dřív net vs. gross → falešné „vše uhrazeno").
  const outstanding = inv.outstanding_gross || 0;
  const allCollected = (inv.invoiced_total || 0) > 0 && outstanding < 1;
  const pending = eco.labor_pending || {};
  // 280: schválené hodiny lidí bez hodinové sazby — v nákladech chybí, marže vychází líp, než je.
  const missing = eco.labor_missing_rate || {};
  const missingH = String(missing.hours || 0).replace(".", ",");
  const supAdv = eco.supplier_advances || {};   // 291: zaplacené zálohy dodavatelům — náklad až konečnou fakturou
  const invoicedSub = [
    ...(inv.invoiced_pct != null ? [`${inv.invoiced_pct} % ceny díla`] : []),
    ...((inv.advances_total || 0) > 0 ? [`nevyúčtované zálohy ${formatCZKCompact(inv.advances_total)}`] : []),
    ...(creditedInvoiced > 0 ? [`− dobropisy ${formatCZKCompact(creditedInvoiced)}`] : []),
    vatWord,
  ].join(" · ");
  const marginSub = `${formatCZKCompact(eco.contract_price)} − ${formatCZKCompact(eco.total_costs)}`;
  const realCollected = vp ? (inv.collected_net || 0) : (inv.collected_total || 0);
  const realSub = `inkasováno ${vp ? "bez DPH " : ""}${formatCZKCompact(realCollected)} − náklady ${formatCZKCompact(eco.total_costs)}`
    + (inv.invoiced_pct != null ? ` · vyfakturováno ${inv.invoiced_pct} %` : "");
  const vatBalance = vat.balance || 0;

  // Donut: barvy podle indexu, 7.+ kategorie se skládá do „Ostatní" (129/C3).
  const catEntries = Object.entries(eco.by_category || {}).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  const pieData = catEntries.length > 6
    ? [...catEntries.slice(0, 6), { name: "Ostatní", value: catEntries.slice(6).reduce((s, x) => s + x.value, 0) }]
    : catEntries;
  const pieColor = (e, i) => (i >= 6 ? "hsl(var(--muted-foreground))" : PIE_COLORS[i]);
  // 365: náklady podle štítků dokladů; doklad bez štítku šedě, 8.+ štítek do „Ostatní"
  const tagEntries = Object.entries(eco.by_tag || {}).filter(([, v]) => v > 0)
    .map(([k, value]) => ({ name: k || "Bez štítku", none: !k, value })).sort((a, b) => (a.none - b.none) || b.value - a.value);
  const tagData = tagEntries.length > 8
    ? [...tagEntries.slice(0, 7), { name: "Ostatní", other: true, value: tagEntries.slice(7).reduce((t, x) => t + x.value, 0) }]
    : tagEntries;
  const tagPie = (e) => (e.none || e.other ? "hsl(var(--muted-foreground) / 0.45)" : tagColor(e.name));

  return (
    <div className="space-y-4" data-testid="project-economics">
      {bar}
      <KpiRow heading="Fakturace" icon={Receipt}>
        <StatCard icon={Wallet} label="Cena díla" value={formatCZKCompact(eco.contract_price)} title={formatCZK(eco.contract_price)} sub={`dle smlouvy · ${vatWord}`} testid="kpi-contract" />
        <StatCard icon={FileText} label="Vyfakturováno" value={formatCZKCompact(inv.invoiced_total || 0)} title={formatCZK(inv.invoiced_total || 0)}
          sub={invoicedSub} subClass={over ? AMBER : undefined} testid="kpi-invoiced" />
        <StatCard icon={Banknote} label="Inkasováno" value={formatCZKCompact(inv.collected_total || 0)} title={formatCZK(inv.collected_total || 0)}
          valueClass={allCollected ? "text-success" : ""}
          sub={`${allCollected ? "vše uhrazeno" : (outstanding >= 1 ? `zbývá inkasovat ${formatCZKCompact(outstanding)}` : "")}${(allCollected || outstanding >= 1) ? " · " : ""}s DPH`}
          testid="kpi-collected" />
        <StatCard icon={ClipboardList} label="Zbývá vyfakturovat" value={formatCZKCompact(inv.remaining_to_invoice || 0)} title={formatCZK(inv.remaining_to_invoice || 0)}
          valueClass={over ? AMBER : ""} sub={over ? `nad cenu díla o ${formatCZKCompact(inv.over_invoiced)} · ${vatWord}` : vatWord} subClass={over ? AMBER : undefined}
          testid="kpi-remaining-invoice" />
      </KpiRow>

      <KpiRow heading="Náklady a marže" icon={TrendingUp}>
        <StatCard icon={Package} label="Náklady celkem" value={formatCZKCompact(eco.total_costs)} title={formatCZK(eco.total_costs)}
          sub={(pending.count || 0) > 0 ? (
            <Link to="/" className="hover:underline" data-testid="kpi-costs-pending"
              title={`${pending.count} směn čeká na schválení v Přehledu — do nákladů se započtou až po schválení.`}>
              + {formatCZKCompact(pending.cost || 0)} čeká na schválení
            </Link>
          ) : (missing.hours || 0) > 0 ? (
            <Link to="/tym" className="hover:underline" data-testid="kpi-costs-missing-rate"
              title={`Bez hodinové sazby: ${(missing.people || []).join(", ")}. Jejich ${missingH} h je v nákladech za 0 Kč — doplňte sazbu v Týmu.`}>
              {missingH} h bez sazby<span className="hidden lg:inline"> · chybí v nákladech</span>
            </Link>
          ) : (supAdv.paid || 0) > 0 ? (
            <span data-testid="kpi-costs-advances"
              title={`Zaplacené zálohy dodavatelům (${supAdv.count}) — peníze odešly, náklad vznikne až konečnou fakturou.`}>
              + zálohy dodavatelům {formatCZKCompact(supAdv.paid)}
            </span>
          ) : `práce + nákupy · ${vatWord}`}
          subClass={(pending.count || 0) > 0 || (missing.hours || 0) > 0 ? AMBER : undefined} testid="kpi-costs" />
        <StatCard icon={Wallet} label="Zbývá z rozpočtu" value={formatCZKCompact(eco.remaining_budget)} title={formatCZK(eco.remaining_budget)}
          valueClass={eco.remaining_budget < 0 ? "text-destructive" : ""} sub={`čerpání ${drawPct} % · ${vatWord}`} testid="kpi-budget-left" />
        <StatCard icon={marginPositive ? TrendingUp : TrendingDown} label="Marže dle smlouvy" value={formatCZKCompact(eco.margin)} title={formatCZK(eco.margin)}
          valueClass={marginPositive ? "text-success" : "text-destructive"} sub={marginSub} testid="kpi-margin" />
        <StatCard icon={Scale} label="Reálný výsledek" value={formatCZKCompact(inv.real_result || 0)} title={formatCZK(inv.real_result || 0)}
          valueClass={(inv.real_result || 0) >= 0 ? "text-success" : "text-destructive"} sub={realSub} testid="kpi-real-result" />
      </KpiRow>

      {vp && (
        <KpiRow heading="DPH" icon={Scale}>
          <StatCard icon={ArrowDownLeft} label="DPH na vstupu" value={formatCZKCompact(vat.input_total || 0)} title={formatCZK(vat.input_total || 0)}
            sub={(vat.input_unknown_count || 0) > 0 ? `u ${vat.input_unknown_count} dokladů DPH neuvedeno` : "odpočet z nákladů"}
            subClass={(vat.input_unknown_count || 0) > 0 ? AMBER : undefined} testid="kpi-vat-input" />
          <StatCard icon={ArrowUpRight} label="DPH na výstupu" value={formatCZKCompact(vat.output_total || 0)} title={formatCZK(vat.output_total || 0)}
            sub="z vydaných faktur" testid="kpi-vat-output" />
          <StatCard icon={Scale} label="Saldo DPH" value={formatCZKCompact(Math.abs(vatBalance))} title={formatCZK(vatBalance)}
            valueClass={vatBalance < 0 ? "text-success" : (vatBalance > 0 ? "text-destructive" : "")}
            sub={vatBalance < 0 ? "stát vám vrátí" : (vatBalance > 0 ? "odvedete státu" : "vyrovnáno")} testid="kpi-vat-balance" />
        </KpiRow>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DonutCard title="Náklady dle kategorie" total={eco.total_costs} sub={`práce + nákupy · ${vatWord}`} data={pieData} colorOf={pieColor}
          testid="category-donut-card" legendTestid="category-legend" empty="Zatím žádné náklady."
          extra={negCats < 0 && (
            <div className="flex items-center gap-2 text-sm pt-2 border-t border-border mt-1" data-testid="pie-credits-line">
              <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-muted-foreground/40" />
              <span className="text-muted-foreground truncate">Dobropisy</span>
              <span className="ml-auto font-semibold tabular-nums whitespace-nowrap text-destructive">−{formatCZK(Math.abs(negCats))}</span>
            </div>
          )} />
        <DonutCard title="Náklady dle štítků" info="Štítek dokladu říká, na co náklad je (Střecha, Elektro, Vícepráce). Práce lidí z docházky má štítek Práce sama, doklad bez štítku je šedě."
          total={eco.total_costs} sub={`podle štítků dokladů · ${vatWord}`} data={tagData} colorOf={tagPie}
          testid="tag-donut-card" legendTestid="tag-legend" empty="Zatím žádné náklady." />
      </div>

      <ProjectTimeChart costSeries={eco.series || []} invoicedSeries={inv.series || []} invoiced={inv.invoiced_total || 0}
        costs={eco.total_costs || 0} contract={eco.contract_price || 0} budget={plannedBudget} vatWord={vatWord} />
    </div>
  );
}
